import { useQuery } from '@apollo/client';
import { css } from '@emotion/core';
import { Avatar, List, ListItem, ListItemAvatar, ListItemText, Theme, useTheme } from '@material-ui/core';
import { Business } from '@material-ui/icons';
import { Pagination, Skeleton } from '@material-ui/lab';
import _ from 'lodash';
import React from 'react';

import { CrunchbaseCompanyFilters } from 'shared/common/crunchbase-filters';

import { currencyFormat } from 'shared/common/string-format-utils';
import { SearchTextField } from '../../common/search-text-field';
import {
    CRUNCHBASE_COMPANIES,
    CRUNCHBASE_COMPANIES_COUNT,
    CrunchbaseCompany,
    CrunchbaseCompanyFilterQueryParams
} from '../../graphql/queries/crunchbase';
import { getGraphRequestParams } from './common';

const pageSize = 10;
const skeletonRows = 10;
const DEBOUNCE_DELAY_MS = 500;

const styles = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    height: 100%;

    div::-webkit-scrollbar {
        width: 5px;
    }

    div::-webkit-scrollbar-thumb {
        border-radius: 2px;
    }

    .results-list {
        flex: 1 1 auto;
        overflow-y: auto;
        margin: 10px 0 20px;
        padding-right: 10px;

        .MuiListItem-root {
            border-bottom: thin solid ${theme.palette.divider};

            .MuiAvatar-root {
                width: 28px;
                height: 28px;

                .MuiSvgIcon-root {
                    font-size: 16px;
                }
            }

            .MuiSkeleton-root.MuiSkeleton-rect {
                width: calc(100% + 25px);
                height: 25px;
                margin: 5px -15px;
            }

            .cb-company-secondary {
                .desc {
                    font-size: 14px;
                }
            }

            .cb-info-chips {
                display: flex;
                flex-wrap: wrap;

                .cb-info-chip {
                    margin-right: 10px;
                    padding-left: 10px;
                    border-left: thin solid ${theme.palette.divider};
                    display: flex;
                    font-size: 12px;
                    line-height: 18px;
                    margin-top: 4px;

                    &:first-child {
                        padding-left: 0;
                        border-left: none;
                    }

                    .category {
                        opacity: 0.5;
                    }

                    .value {
                        padding-left: 2px;
                    }
                }
            }
        }
    }

    .results-pagination {
        flex: 0 0 auto;
        display: flex;
        justify-content: center;
    }

    .results-search {
        flex: 0 0 auto;

        .search.outlined {
            padding: 4px 0;
        }

        .MuiInputBase-root {
            flex: 1 1 auto;
        }
    }
`;

export const CBPreview: React.FC<{ filters: CrunchbaseCompanyFilters; totalCount: number }> = ({ filters }) => {
    const theme = useTheme();
    const [searchText, setSearchText] = React.useState('');
    const [debouncedSearchText, setDebouncedSearchText] = React.useState('');
    const [page, setPage] = React.useState(0);

    // Apply the base filters from props
    const baseWhereClause = getGraphRequestParams(filters);

    // Add the name filter if there's a search text
    const whereClause = React.useMemo(() => {
        if (debouncedSearchText) {
            return {
                ...baseWhereClause,
                lower_name: { _ilike: `%${debouncedSearchText.toLowerCase()}%` }
            };
        }
        return baseWhereClause;
    }, [baseWhereClause, debouncedSearchText]);

    // Set up the debounced search effect
    React.useEffect(() => {
        const handler = _.debounce(() => {
            setDebouncedSearchText(searchText);
            setPage(0);
        }, DEBOUNCE_DELAY_MS);

        handler();

        return () => {
            handler.cancel();
        };
    }, [searchText]);

    const { data } = useQuery<
        { companies: CrunchbaseCompany[] },
        { where: CrunchbaseCompanyFilterQueryParams; limit: number; offset: number }
    >(CRUNCHBASE_COMPANIES, { variables: { where: whereClause, limit: pageSize, offset: page * pageSize } });
    const { data: totalCountData } = useQuery<
        { companiesCount: { aggregate: { count: string } } },
        { where: CrunchbaseCompanyFilterQueryParams }
    >(CRUNCHBASE_COMPANIES_COUNT, { variables: { where: whereClause } });

    const handleChangePage = (_1: any, newPage: number) => setPage(newPage - 1);

    const skeletonRowsArray = !data
        ? _.times(skeletonRows, (i) => (
              <ListItem alignItems="flex-start" key={i}>
                  <Skeleton variant="rect" />
              </ListItem>
          ))
        : null;

    const rows = data?.companies?.map((company) => {
        const cbUrl = company.permalink ? `https://www.crunchbase.com/organization/${company.permalink}` : undefined;
        const logoUrl = company.imageId
            ? `https://images.crunchbase.com/image/upload/t_cb-default-original/${company.imageId}`
            : undefined;
        const avatar = logoUrl ? (
            <Avatar src={logoUrl} alt={company.name} />
        ) : (
            <Avatar>
                <Business />
            </Avatar>
        );
        const secondary = (
            <div className="cb-company-secondary">
                <div className="desc">{company.description}</div>
                <div className="cb-info-chips">
                    <div className="cb-info-chip">
                        <div className="category">Last Round:</div>
                        <div className="value">{_.startCase(company.lastFundingType)}</div>
                    </div>
                    <div className="cb-info-chip">
                        <div className="category">Total Funding:</div>
                        <div className="value">{currencyFormat(company.funding, 0)}</div>
                    </div>
                    <div className="cb-info-chip">
                        <div className="category">Size:</div>
                        <div className="value">{company.employees}</div>
                    </div>
                </div>
            </div>
        );
        const primary = (
            <a href={cbUrl} target="_blank" rel="noopener noreferrer">
                {company.name}
            </a>
        );
        return (
            <ListItem alignItems="flex-start" key={company.uuid}>
                <ListItemAvatar>
                    <a href={cbUrl} target="_blank" rel="noopener noreferrer">
                        {avatar}
                    </a>
                </ListItemAvatar>
                <ListItemText primary={primary} secondary={secondary} disableTypography={true} />
            </ListItem>
        );
    });

    const pageCount = Math.ceil(Number(totalCountData?.companiesCount?.aggregate?.count) / pageSize);
    const pagination = data ? <Pagination count={pageCount} page={page + 1} onChange={handleChangePage} /> : null;
    return (
        <div css={styles(theme)}>
            <div className="results-search">
                <SearchTextField value={searchText} onValueChange={setSearchText} variant="outlined" />
            </div>
            <div className="results-list">
                <List dense={true}>
                    {skeletonRowsArray}
                    {rows}
                </List>
            </div>
            <div className="results-pagination">{pagination}</div>
        </div>
    );
};
